/**
 * Resolve a content-free template into the values a new object starts with.
 *
 * `deriveTemplate` names the roles a pool is unanimous about and can never know
 * what it is unanimous ABOUT — `ObjectShape` carries counts, not values, and
 * `assertShapeCarriesNoValues` exists to keep it that way. So the last step is
 * the host's: look up what those roles actually hold, in the store, where
 * values legitimately live and nothing is pooled.
 *
 * It lives in its own file rather than inside `main.ts` because three of the
 * four defects that got the first attempt reverted were HERE — a runtime-minted
 * key dropped, a boolean-backed enum coerced away, a preset that could have
 * been hardcoded without any test noticing. A function reachable only through a
 * button is a function no unit test can reach, and that is the property that
 * let those three through. Its three inputs are injected for the same reason.
 *
 * @Codex's, on task 0092 piece 2.
 */
import { deriveTemplate, resolveParams, type ObjectShape } from '@morph/generation';
import type { Role } from '@morph/spec';
import type { PropertyKeyId, StoredObject, Value } from '@morph/store';

export async function resolveTemplateSeed(
  shape: ObjectShape,
  objects: AsyncIterable<StoredObject>,
  keyFor: (role: Role) => PropertyKeyId | undefined,
): Promise<readonly (readonly [PropertyKeyId, Value])[]> {
  const wanted = new Set(deriveTemplate(shape, resolveParams()).unanimous);
  if (!wanted.size) return [];

  // The tagged `Value` is carried through UNTOUCHED. `value.ts` maps
  // `{ k: 'bool' }` to kind `enum`, so a checkbox convention can be unanimous —
  // and the reverted attempt's `typeof value !== 'string' && !== 'number'`
  // guard threw exactly those away. There is no longer a guard to throw
  // anything away with.
  const values = new Map<Role, Value>();
  for await (const object of objects) {
    for (const role of wanted) {
      const value = object.roles.get(role)?.[0];
      if (value !== undefined) values.set(role, value);
    }
    if (values.size === wanted.size) break;
  }

  const seed: (readonly [PropertyKeyId, Value])[] = [];
  for (const role of wanted) {
    // `keyFor` resolves the way `writeProperty` resolves — the pool's static
    // defs FIRST, then keys minted at runtime through Add Property. Reading
    // only the static defs made the one path a person can actually use to
    // establish a convention the one path that did not work.
    const key = keyFor(role);
    const value = values.get(role);
    if (key !== undefined && value !== undefined) seed.push([key, value]);
  }
  return seed;
}
